import { GrowthBook } from "@growthbook/growthbook";
import { autoAttributesPlugin } from "@growthbook/growthbook/plugins";

const API_HOST =
	process.env.GATSBY_GROWTHBOOK_API_HOST || "https://cdn.growthbook.io";
const CLIENT_KEY = process.env.GATSBY_GROWTHBOOK_CLIENT_KEY;
const ENABLE_DEV_MODE = process.env.GATSBY_GROWTHBOOK_DEV_MODE === "true";

const plugins = typeof window !== "undefined" ? [autoAttributesPlugin()] : [];

// Cache for feature values to avoid repeated calls
const featureValueCache = new Map();

const clearFeatureCache = () => {
	featureValueCache.clear();
};

const growthbook = new GrowthBook({
	apiHost: API_HOST,
	clientKey: CLIENT_KEY,
	enableDevMode: ENABLE_DEV_MODE,
	trackingCallback: (experiment, result) => {
		if (typeof window === "undefined") {
			return;
		}

		const analytics = window.analytics;
		if (!analytics || typeof analytics.track !== "function") {
			return;
		}

		// Use analytics utility for consistent error handling
		import("../utils/analytics").then(({ default: analyticsUtil }) => {
			analyticsUtil.track("$experiment_started", {
				"Experiment name": experiment.key,
				"Variant name": result.key,
				$source: "growthbook",
			});
		});
	},
	plugins,
});

growthbook.subscribe(() => {
	clearFeatureCache();
});

let initPromise;

const initGrowthBook = () => {
	if (typeof window === "undefined") {
		return Promise.resolve();
	}

	if (!CLIENT_KEY) {
		return Promise.resolve();
	}

	if (!initPromise) {
		initPromise = growthbook
			.init({ streaming: true })
			.catch((error) => {
				if (ENABLE_DEV_MODE && typeof console !== "undefined") {
					console.warn("GrowthBook streaming init failed", error);
				}

				return growthbook.loadFeatures({ autoRefresh: true }).catch(
					(loadError) => {
						if (ENABLE_DEV_MODE && typeof console !== "undefined") {
							console.warn("GrowthBook feature fetch failed", loadError);
						}
					},
				);
			})
			.then(() => {
				clearFeatureCache();
			});
	}

	return initPromise;
};

const getGrowthBookValue = (featureKey, fallback = undefined) => {
	// Check cache first
	if (featureValueCache.has(featureKey)) {
		return featureValueCache.get(featureKey);
	}

	try {
		const value = growthbook.getFeatureValue(featureKey, fallback);
		// Cache the value
		featureValueCache.set(featureKey, value);
		return value;
	} catch {
		if (ENABLE_DEV_MODE && typeof console !== "undefined") {
			console.warn(`GrowthBook evaluation failed for "${featureKey}"`);
		}
		// Cache the fallback value
		featureValueCache.set(featureKey, fallback);
		return fallback;
	}
};

export { growthbook, initGrowthBook, getGrowthBookValue, clearFeatureCache };
